import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Table, Badge, Empty, Button, Input, Select, Field, SortControl, sortRows } from "@/lib/ui";

const BLANK = { seller_type: "retailer", company_name: "", city: "", locality: "", contact_person: "", whatsapp: "", email: "" };

export default function IncompleteSignups() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ field: "company_name", dir: "asc" });
  const SORT_FIELDS = [
    { key: "created_at", label: "Registered", type: "date" },
    { key: "company_name", label: "Company", type: "str" },
    { key: "phone", label: "Mobile", type: "str" },
    { key: "seller_type", label: "Type", type: "str" },
  ];

  const load = () => api.get("/admin/incomplete-signups").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const openEdit = (s) => setEditing({ id: s.id, phone: s.phone, ...BLANK, ...{
    seller_type: s.seller_type || "retailer", company_name: s.company_name || "", city: s.city || "",
    locality: s.locality || "", contact_person: s.contact_person || "", whatsapp: s.whatsapp || "", email: s.email || "" } });

  const save = async () => {
    try {
      await api.put(`/admin/sellers/${editing.id}`, {
        seller_type: editing.seller_type, company_name: editing.company_name, city: editing.city,
        locality: editing.locality, contact_person: editing.contact_person, whatsapp: editing.whatsapp, email: editing.email,
      });
      toast.success("Seller updated"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const resetPw = async (s) => {
    if (!window.confirm(`Reset password for +91 ${s.phone} to their mobile number?`)) return;
    try { await api.post(`/admin/sellers/${s.id}/reset-password`); toast.success("Password reset to mobile number"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete signup +91 ${s.phone}?`)) return;
    try { await api.delete(`/admin/sellers/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remind = (s) => {
    const name = s.company_name ? ` ${s.company_name}` : "";
    const msg = `Hi${name}, this is Deadstock.live. We noticed you registered as a seller but haven't completed your company profile yet. Please finish your registration to start listing your stock and reach buyers across India. Need help? Just reply here.`;
    window.open(`https://wa.me/91${s.phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const setF = (k, v) => setEditing((s) => ({ ...s, [k]: v }));

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Incomplete Signups</h1>
      <p className="mt-1 text-sm text-slate-500">Sellers who registered but did not finish the company profile / onboarding.</p>
      {rows.length === 0 ? <div className="mt-6"><Empty text="No incomplete signups — everyone finished onboarding" icon="fa-user-check" /></div> : (
        <div className="mt-4">
          <div className="mb-3"><SortControl fields={SORT_FIELDS} value={sort} onChange={setSort} testid="incomplete-sort" /></div>
          <Table head={["Mobile", "Seller Type", "Company (partial)", "Password Changed", "Registered", "Actions"]}>
            {sortRows(rows, SORT_FIELDS, sort).map((s) => (
              <tr key={s.id} data-testid={`incomplete-${s.id}`}>
                <td className="px-4 py-3 font-semibold text-slate-800">+91 {s.phone}</td>
                <td className="px-4 py-3 capitalize">{s.seller_type}</td>
                <td className="px-4 py-3">{s.company_name || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3"><Badge tone={s.must_change_password ? "amber" : "green"}>{s.must_change_password ? "Not yet" : "Yes"}</Badge></td>
                <td className="px-4 py-3 text-slate-500">{s.created_at ? new Date(s.created_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button variant="green" onClick={() => remind(s)} data-testid={`incomplete-remind-${s.id}`} title="Remind on WhatsApp to complete registration"><i className="fa-brands fa-whatsapp" /></Button>
                    <Button variant="outline" onClick={() => openEdit(s)} data-testid={`incomplete-edit-${s.id}`}><i className="fa-solid fa-pen" /></Button>
                    <Button variant="ghost" onClick={() => resetPw(s)} title="Reset password to mobile"><i className="fa-solid fa-key text-amber-600" /></Button>
                    <Button variant="ghost" onClick={() => del(s)} data-testid={`incomplete-del-${s.id}`}><i className="fa-solid fa-trash text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 ds-scroll" onClick={(e) => e.stopPropagation()} data-testid="incomplete-edit-modal">
            <h2 className="font-head text-xl font-extrabold text-slate-900">Complete / Edit Seller</h2>
            <p className="text-xs text-slate-400">+91 {editing.phone}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field label="Seller Type"><Select value={editing.seller_type} onChange={(e) => setF("seller_type", e.target.value)}><option value="retailer">Retailer</option><option value="wholesaler">Wholesaler</option><option value="manufacturer">Manufacturer</option></Select></Field>
              <Field label="Company Name"><Input value={editing.company_name} onChange={(e) => setF("company_name", e.target.value)} data-testid="incomplete-company" /></Field>
              <Field label="City"><Input value={editing.city} onChange={(e) => setF("city", e.target.value)} /></Field>
              <Field label="Locality"><Input value={editing.locality} onChange={(e) => setF("locality", e.target.value)} /></Field>
              <Field label="Contact Person"><Input value={editing.contact_person} onChange={(e) => setF("contact_person", e.target.value)} /></Field>
              <Field label="WhatsApp"><Input value={editing.whatsapp} onChange={(e) => setF("whatsapp", e.target.value.replace(/\D/g, "").slice(0, 10))} /></Field>
              <Field label="Email"><Input value={editing.email} onChange={(e) => setF("email", e.target.value)} /></Field>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} data-testid="incomplete-save">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
